import React from "react";
import { Link, useLocation, Outlet, Navigate } from "react-router-dom";
import { useAuth, hasMinRole } from "@/context/AuthContext";
import { cn } from "@/lib/utils";
import { LogOut, LayoutGrid, Landmark, Loader2 } from "lucide-react";

export default function ForumShell() {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  React.useEffect(() => { document.title = "Forum | GovFlowState"; }, []);
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-brand-bg text-slate-500 gap-2"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  const isStaff = hasMinRole(user?.role, "analyst");

  return (
    <div className="min-h-screen bg-brand-bg flex flex-col">
      <a href="#forum-main" className="skip-link" data-testid="skip-to-main">Skip to main content</a>
      <header className="sticky top-0 z-30 bg-brand-navy text-white shadow-card"
        style={{ backgroundImage: "linear-gradient(90deg, #123B63 0%, #0E3252 100%)" }}>
        <div className="mx-auto max-w-5xl px-4 sm:px-6 h-16 flex items-center gap-3">
          <Link to="/forum" className="flex items-center gap-2.5" data-testid="forum-home-link">
            <div className="h-9 w-9 rounded-lg bg-white flex items-center justify-center shrink-0 ring-1 ring-white/40 overflow-hidden">
              <img src="/govflow-mark.png" alt="GovFlowState" className="h-7 w-7 object-contain" />
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-[15px] tracking-tight">GovFlowState</div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-brand-gold flex items-center gap-1"><Landmark className="h-3 w-3" /> Forum</div>
            </div>
          </Link>
          <nav className="ml-4 hidden sm:flex items-center gap-1" aria-label="Forum">
            <Link to="/forum" data-testid="forum-nav-myforum"
              className={cn("rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
                location.pathname === "/forum" ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/[0.06]")}>
              My Forum
            </Link>
          </nav>
          <div className="ml-auto flex items-center gap-3">
            {isStaff && (
              <Link to="/my-work" data-testid="switch-to-staff"
                className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-white/25 px-3 py-1.5 text-xs font-semibold text-white/85 hover:bg-white/10">
                <LayoutGrid className="h-3.5 w-3.5" /> Staff Workspace
              </Link>
            )}
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-brand-civic flex items-center justify-center text-xs font-bold ring-1 ring-white/20 overflow-hidden">
                {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0] || "?")}
              </div>
              <span className="hidden sm:block text-sm font-medium text-white/90" data-testid="forum-user-name">{user?.name}</span>
              <button onClick={logout} data-testid="forum-logout" aria-label="Sign out"
                className="text-white/60 hover:text-red-300 transition-colors"><LogOut className="h-[18px] w-[18px]" /></button>
            </div>
          </div>
        </div>
      </header>
      <main id="forum-main" className="flex-1 mx-auto w-full max-w-5xl px-4 sm:px-6 py-8" data-testid="forum-main">
        <Outlet />
      </main>
    </div>
  );
}
